import { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import IGetPickListsDTO from "../../../models/DTO/IGetPickListsDTO";
import IOnGetPickListsDTO from "../../../models/DTO/IOnGetPickListsDTO";
import IOnUpdatePickListDTO from "../../../models/DTO/IOnUpdatePickListDTO";
import IUpdatePickListDTO from "../../../models/DTO/IUpdatePickListDTO";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IOwTableCols from "../../../models/IOwTableCols";
import IPick_List from "../../../models/IPick_List";
import StatusEnum from "../../../models/StatusEnum";
import UserTypesEnum from "../../../models/UserTypesEnum";
import UserService from "../../../services/UserService";
import Breadcrumbs from "../../shared/Breadcrumbs/Breadcrumbs";
import OwButton from "../../shared/OwButton/OwButton";
import OwLabel from "../../shared/OwLabel/OwLabel";
import { OwStatusEnumIcon } from "../../shared/OwStatusEnumIcon/OwStatusEnumIcon";
import OwTable from "../../shared/OwTable/OwTable";
import OwTitle from "../../shared/OwTitle/OwTitle";
import PaginationBar from "../../shared/PaginationBar/PaginationBar";
import PickListFilter from "../PickListFilter/PickListFilter";
import PickListSidebar from "../PickListSidebar/PickListSidebar";
import "./PickList.scss";
import { OwDeleteEditActions } from "../../shared/OwDeleteEditActions/OwDeleteEditActions";
import { ConfirmDeleteOverlayFn } from "../../shared/ConfirmDeleteOverlayFn/ConfirmDeleteOverlayFn";
import UiContext from "../../../contexts/UiContext";
import UserContext from "../../../contexts/UserContext";
import { SocketContext } from "../../../contexts/SocketContext";
import SupplierContext from "../../../contexts/SupplierContext";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import INotification from "../../../models/INotification";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import {SearchSection} from "../../shared/SearchSection/SearchSection";

type Props = {};

export const PickList = (props: Props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);

    const history = useNavigate();

    const ordersRenderFn = (row: IPick_List) => {
        if (row.Orders) {
            const ordersDivs = row.Orders.map((order) => {
                return (
                    <div key={order.guid}>
                        <Link to={`/orders/${order.guid}/entry`}>{order.name}</Link>
                    </div>
                );
            });

            return <div>{ordersDivs}</div>;
        }
    };

    const poNoRenderFn = (row: IPick_List) => {
        if (row.Orders) {
            const poDiv = (
                <div>
                    {row.Orders.map((order) => {
                        return <div key={order.guid}>{order.po_no}</div>;
                    })}
                </div>
            );

            return poDiv;
        }
    };

    const actionsRenderFn = (row: IPick_List, index: number) => {
        return (
            <div>
                {userModel.supplierUserToken?.user_type_id === UserTypesEnum.ADMIN ? (
                    <OwDeleteEditActions
                        isEditMode={false}
                        onTrashClickFn={(e: any) => {
                            confirmDelete(row, index);
                        }}
                        onEditClickFn={() => {}}
                        hideEdit={true}
                    />
                ) : null}
            </div>
        );
    };

    const nameRenderFn = (row: IPick_List) => {
        return (
            <div>
                <i
                    className={"me-2 fad " + (row.is_locked ? "fa-lock-alt" : "fa-lock-open-alt")}
                    title={row.is_locked ? "Locked" : "Unlocked"}
                />
                <Link to={`/pick_lists/${row.guid}`}>{row.name}</Link>
            </div>
        );
    };

    const renderStatusFn = (status_id: StatusEnum) => {
        return <OwStatusEnumIcon status={status_id} />;
    };

    const vendorNameTitleRenderFn = (col: IOwTableCols) => {
        return <OwLabel title={"vendor"} supplierModel={supplierModel} isSupplierVendorOverride={true} />;
    };

    const vendorNameRenderFn = (row: IPick_List) => {
        const vendorNames = (row.Orders || []).map((order) => {
            return <div key={order.guid}>{order.Vendor?.name || "--"}</div>;
        });

        return <div>{vendorNames}</div>;
    };

    const dateRenderFn = (dateStr: string) => {
        return UserService.parseDate(dateStr);
    };

    const getTableCols = (): IOwTableCols[] => {
        return [
            {
                isActionsCol: true,
                title: "",
                valueProp: "",
                renderFn: actionsRenderFn,
                renderFnEmitsRow: true,
            },
            {
                title: "name",
                valueProp: "name",
                renderFn: nameRenderFn,
                renderFnEmitsRow: true,
            },
            {
                title: "total_items",
                valueProp: "total_items",
                className: "text-center",
            },
            {
                title: "status",
                valueProp: "status_id",
                renderFn: renderStatusFn,
            },
            {
                title: "vendor",
                valueProp: "Vendor",
                valueSubProp: "name",
                titleRenderFn: vendorNameTitleRenderFn,
                renderFn: vendorNameRenderFn,
                renderFnEmitsRow: true,
            },
            {
                title: "orders",
                valueProp: "",
                renderFn: ordersRenderFn,
                renderFnEmitsRow: true,
            },
            {
                title: "po_no",
                valueProp: "",
                renderFn: poNoRenderFn,
                renderFnEmitsRow: true,
            },
            {
                title: "created_at",
                valueProp: "created_at",
                renderFn: dateRenderFn,
            },
            {
                title: "updated_at",
                valueProp: "updated_at",
                renderFn: dateRenderFn,
            },
        ];
    };

    const [tableCols, setTableCols] = useState(getTableCols());
    const [crumbs, setCrumbs] = useState([
        { title: "Dashboard", url: "/dashboard" },
        { title: "Pick Lists", url: "/pick_lists" },
    ]);
    const [isDataFetching, setIsDataFetching] = useState(false);
    const [isInitialData, setIsInitialData] = useState(true);
    const [pickLists, setPickLists] = useState<IPick_List[]>([]);
    const [deleteElem, setDeleteElem] = useState<string | null>(null);
    const [deleteRow, setDeleteRow] = useState<IPick_List | null>(null);
    const [offset, setOffset] = useState(0);
    const [limit, setLimit] = useState(10);
    const [totalRows, setTotalRows] = useState(0);
    const [queryString, setQueryString] = useState(`filter_completed=true`);

    const confirmDelete = (row: IPick_List, index: number) => {
        setDeleteElem(`row-${index}`);
        setDeleteRow(row);
    };

    const onUpdatePickList = (data: IOnUpdatePickListDTO) => {
        const notification: INotification = {
            message: "successfully_updated_pick_list",
            position: NotificationPositionEnum.bc,
            level: NotificationLevelEnum.success,
        };

        uiContext.notifyFn(null, notification);

        setDeleteElem(null);
        setDeleteRow(null);

        getPickLists();
    };

    const onGetPickLists = (data: IOnGetPickListsDTO) => {
        setPickLists(data.pickLists);
        setIsDataFetching(false);
        setIsInitialData(false);
        setTotalRows(data.totalRows);
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.OrderOnGetPickLists, fn: onGetPickLists },
        { endpoint: EndpointsEnum.OrderOnUpdatePickList, fn: onUpdatePickList },
    ];

    useEffect(() => {
        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        return () => {
            for (const _socket of sockets) {
                socket.removeListener(_socket.endpoint, _socket.fn);
            }
        };
    }, []);

    useEffect(() => {
        // this is double calling on load, but i don't care to fix it right now.
        getPickLists(queryString);
    }, [queryString, offset, limit]);

    const getPickListsWithFilter = (queryString?: string) => {
        setQueryString(queryString || "");
    };

    const getPickLists = (_queryString?: string) => {
        const payload: IGetPickListsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            queryString: _queryString ? _queryString : queryString,
            offset,
            limit,
        };

        socket.emit(EndpointsEnum.OrderGetPickLists, payload);

        setIsDataFetching(true);
    };

    const closeSidebar = () => {
        uiContext.toggleSidebar(false);

        getPickLists();
    };

    const openSidebar = (pickList?: IPick_List) => {
        const sidebarComponent = (
            <PickListSidebar
                cancel={closeSidebar}
                userModel={userModel}
                supplierModel={supplierModel}
                socket={socket}
                notifyFn={uiContext.notifyFn}
            />
        );

        uiContext.setSidebarComponent(sidebarComponent);

        uiContext.toggleSidebar(true);
    };

    const cancelDelete = () => {
        setDeleteElem(null);
        setDeleteRow(null);
    };

    const commitDelete = () => {
        if (deleteRow) {
            const payload: IUpdatePickListDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                deleteFlag: true,
                pickListGuid: deleteRow.guid,
                catalogItemsPicked: [],
            };

            socket.emit(EndpointsEnum.OrderUpdatePickList, payload);

            setDeleteElem(null);
            setDeleteRow(null);
        }
    };

    const paginationChanged = (_offset: number, _limit: number) => {
        setOffset(_offset);
        setLimit(_limit);
    };

    return (
        <div className="PickList position-relative">

            <div className="row mt-2 d-print-none">
                <div className="col-12 d-flex align-items-center justify-items-center">
                    <OwTitle className={"me-3"} title="pick_lists" leftIconName="fad fa-hand-holding-box" />
                    <OwButton
                        className={"btn-warning"}
                        title={"create"}
                        leftIconClass={"fas fa-plus"}
                        fn={() => {
                            openSidebar();
                        }}
                    />
                </div>
            </div>

            <div className={"mt-2 mb-2 d-print-none"}>
                <SearchSection>
                    <PickListFilter
                        socket={socket}
                        searchFn={getPickListsWithFilter}
                        supplierModel={supplierModel}
                        userModel={userModel}
                        history={history}
                    />
                </SearchSection>

            </div>

            <div className={"d-none d-md-block position-relative"}>
                <ConfirmDeleteOverlayFn
                    targetElem={deleteElem}
                    message={"confirm_delete"}
                    handleConfirm={commitDelete}
                    handleCancel={cancelDelete}
                    nestedParentElem={"pick-lists"}
                    skipMainView={true}
                />

                <OwTable
                    id={"pick-lists"}
                    columns={tableCols}
                    dataSet={pickLists}
                    userModel={userModel}
                    supplierModel={supplierModel}
                    dataFetch={isDataFetching}
                    initialData={isInitialData}
                />
            </div>

            <div className={"d-block d-md-none"}>
                {pickLists.map((pickList, index) => {
                    let vendorName: string | null = null;

                    if (pickList.Orders) {
                        const firstOrder = pickList.Orders[0];

                        if (firstOrder?.Vendor) {
                            vendorName = firstOrder.Vendor.name;
                        }
                    }

                    return (
                        <div key={pickList.guid} className={"pickListRow pt-2 pb-2"}>
                            <div className={"col-12 d-flex "}>
                                <div className={"w-80"}>
                                    <div className={"mb-2 "}>
                                        <h5>
                                            <div>
                                                <i
                                                    className={"me-2 fad " + (pickList.is_locked ? "fa-lock-alt" : "fa-lock-open-alt")}
                                                    title={pickList.is_locked ? "Locked" : "Unlocked"}
                                                />
                                                <Link to={`/pick_lists/${pickList.guid}`}>{pickList.name}</Link>
                                            </div>
                                        </h5>
                                    </div>
                                    <div>{vendorName}</div>
                                    <div>
                                        {pickList.Orders?.map((order) => {
                                            return <div key={order.guid}>{order.name}</div>;
                                        })}
                                    </div>
                                </div>
                                <div className={"w-20 text-center"}>
                                    <div>
                                        <div>
                                            <OwLabel title={"items"} />
                                        </div>
                                        <div>{pickList.total_items}</div>
                                    </div>
                                    <div>{pickList.status_id ? <OwStatusEnumIcon status={pickList.status_id} /> : null}</div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div>
                <PaginationBar offset={offset} limit={limit} totalRecords={totalRows} onPaginationChanged={paginationChanged} />
            </div>
        </div>
    );
};
